/**
 * Sessão de voz, vídeo e compartilhamento de tela.
 *
 * A mídia nunca passa pelo nosso servidor: ele apenas emite um token com escopo
 * e o cliente fala direto com o SFU. Quem decide o que pode ser publicado é o
 * token, então esconder um botão aqui é conforto de interface, não segurança.
 */

import { create } from 'zustand'
import {
  ConnectionQuality,
  ConnectionState,
  DisconnectReason,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
  RoomEvent,
  Track,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  type VideoCodec,
} from 'livekit-client'
import type { ScreenSharePresetId, VoiceQualityLimits } from '@nexus/shared'
import { silenciarBandeja } from '@/lib/bandeja-de-sons'
import { usarFonteDeCaptura } from '@/lib/desktop'

/**
 * De onde vem a imagem transmitida.
 *
 * A tela é o caso comum, escolhida no seletor do próprio navegador. O dispositivo
 * cobre placa de captura e câmeras externas: para o navegador elas são apenas
 * mais uma entrada de vídeo, e sem esta opção só dava para publicá-las como
 * câmera, presas ao teto de qualidade da câmera, que é cinco vezes menor.
 */
export type FonteDeTransmissao =
  | {
      tipo: 'tela'
      /**
       * Monitor ou janela escolhido no aplicativo desktop.
       *
       * No navegador não existe: quem pergunta é o próprio navegador, no
       * momento da captura.
       */
      sourceId?: string
    }
  | {
      tipo: 'dispositivo'
      deviceId: string
      /** Entrada de som do mesmo aparelho, quando ele tiver uma. */
      audioDeviceId?: string
    }
import { SCREEN_SHARE_PRESETS } from '@nexus/shared'
import { api } from '@/lib/api'
import { gateway } from '@/lib/gateway'
import {
  DEFAULT_AUDIO_OPTIONS,
  MicrophoneProcessor,
  type AudioProcessingOptions,
  type ProcessedAudio,
} from '@/lib/audio-processing'
import { usePreferencesStore } from './preferences'

export interface RemoteMedia {
  identity: string
  audioTrack: RemoteTrack | null
  cameraTrack: RemoteTrack | null
  screenTrack: RemoteTrack | null
  screenAudioTrack: RemoteTrack | null
  speaking: boolean
  audioLevel: number
  connectionQuality: ConnectionQuality
}

export interface VoiceStats {
  /** Taxa de saída do próprio cliente, em bits por segundo. */
  outboundBps: number
  inboundBps: number
  packetsLostPercent: number
  roundTripMs: number
}

interface VoiceState {
  channelId: string | null
  guildId: string | null
  connectionState: ConnectionState
  connecting: boolean
  error: string | null

  room: Room | null
  limits: VoiceQualityLimits | null

  muted: boolean
  deafened: boolean
  /** Como estava o microfone antes de ensurdecer, para poder devolvê-lo. */
  mudoAntesDeEnsurdecer: boolean
  cameraOn: boolean
  screenSharing: boolean
  /** Preset em uso na transmissão de tela atual. */
  screenPreset: ScreenSharePresetId
  /**
   * Transmissões que esta pessoa escolheu não assistir.
   *
   * A imagem deixa de ser baixada, não só de ser mostrada: uma transmissão em
   * 1080p que ninguém está olhando consome a mesma banda de uma que está sendo
   * assistida, e num celular com dados isso é dinheiro indo embora.
   */
  transmissoesOcultas: string[]
  /** De onde vem a imagem que está sendo transmitida agora. */
  fonteDaTransmissao: FonteDeTransmissao | null

  localAudioLevel: number
  localSpeaking: boolean
  rnnoiseActive: boolean
  /** Motivo de ter entrado sem microfone, quando for o caso. */
  micUnavailable: string | null

  remotes: Record<string, RemoteMedia>
  /** Faixa local de tela, para o próprio usuário ver o que está transmitindo. */
  localScreenTrack: LocalVideoTrack | null
  /** Som que acompanha a transmissão, quando ela vem de um aparelho que tem. */
  localScreenAudioTrack: LocalAudioTrack | null
  localCameraTrack: LocalVideoTrack | null

  stats: VoiceStats

  connect: (channelId: string, guildId: string) => Promise<void>
  disconnect: () => Promise<void>
  toggleMute: () => Promise<void>
  toggleDeafen: () => void
  toggleCamera: () => Promise<void>
  startScreenShare: (preset?: ScreenSharePresetId, fonte?: FonteDeTransmissao) => Promise<void>
  stopScreenShare: () => Promise<void>
  ocultarTransmissao: (identity: string) => void
  assistirTransmissao: (identity: string) => void
  reapplyAudioProcessing: (options: AudioProcessingOptions) => Promise<void>
  setOutputDevice: (deviceId: string) => Promise<void>
}

/**
 * Traduz a falha de acesso ao microfone para algo acionável.
 *
 * O navegador devolve nomes de erro em inglês e mensagens que variam entre
 * navegadores; quem está tentando entrar numa conversa precisa saber o que
 * fazer, não o nome interno da exceção.
 */
function describeMicrophoneError(error: unknown): string {
  const name = error instanceof Error ? error.name : ''

  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Você entrou sem microfone porque o acesso não foi autorizado. Libere o microfone para este site no ícone de cadeado ao lado do endereço e entre de novo.'
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'Você entrou sem microfone porque nenhum foi encontrado. Conecte um microfone e entre de novo.'
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return 'Você entrou sem microfone porque ele está sendo usado por outro programa. Feche o outro aplicativo e entre de novo.'
  }

  return 'Você entrou sem microfone: não foi possível acessá-lo. Você consegue ouvir, mas não falar.'
}

/**
 * Liga ou desliga a chegada da tela de um participante.
 *
 * Vale para a imagem e para o som dela, que são duas faixas com a mesma origem.
 */
function aplicarInscricaoDeTela(room: Room, identity: string, inscrito: boolean): void {
  const participante = room.remoteParticipants.get(identity)
  if (!participante) return

  for (const publication of participante.trackPublications.values()) {
    const eDaTela =
      publication.source === Track.Source.ScreenShare ||
      publication.source === Track.Source.ScreenShareAudio

    if (eDaTela) publication.setSubscribed(inscrito)
  }
}

/** Liga ou desliga a chegada de todo áudio dos outros participantes. */
function aplicarSurdez(room: Room, surdo: boolean): void {
  for (const participant of room.remoteParticipants.values()) {
    for (const publication of participant.trackPublications.values()) {
      if (publication.kind === Track.Kind.Audio) {
        publication.setEnabled(!surdo)
      }
    }
  }
}

const micProcessor = new MicrophoneProcessor()
let processedAudio: ProcessedAudio | null = null
let levelTimer: number | null = null
let statsTimer: number | null = null

const emptyStats: VoiceStats = {
  outboundBps: 0,
  inboundBps: 0,
  packetsLostPercent: 0,
  roundTripMs: 0,
}

export const useVoiceStore = create<VoiceState>((set, get) => ({
  channelId: null,
  guildId: null,
  connectionState: ConnectionState.Disconnected,
  connecting: false,
  error: null,

  room: null,
  limits: null,

  muted: false,
  deafened: false,
  mudoAntesDeEnsurdecer: false,
  cameraOn: false,
  screenSharing: false,
  screenPreset: 'smooth',
  transmissoesOcultas: [],
  fonteDaTransmissao: null,

  localAudioLevel: 0,
  localSpeaking: false,
  rnnoiseActive: false,
  micUnavailable: null,

  remotes: {},
  localScreenTrack: null,
  localScreenAudioTrack: null,
  localCameraTrack: null,

  stats: emptyStats,

  connect: async (channelId, guildId) => {
    const current = get()
    if (current.channelId === channelId && current.room) return
    if (current.room) await get().disconnect()

    set({ connecting: true, error: null, channelId, guildId })

    try {
      const credentials = await api.voice.join(channelId)
      const prefs = usePreferencesStore.getState()

      const room = new Room({
        // Ajusta a qualidade recebida ao tamanho real do elemento de vídeo na
        // tela. Numa grade de miniaturas, isso derruba a banda recebida sem
        // perda visível nenhuma.
        adaptiveStream: true,
        // O SFU só encaminha as camadas que alguém está de fato assistindo.
        dynacast: true,
        disconnectOnPageLeave: true,
        audioCaptureDefaults: {
          echoCancellation: prefs.audio.echoCancellation,
          noiseSuppression: prefs.audio.browserNoiseSuppression,
          autoGainControl: prefs.audio.autoGainControl,
        },
        publishDefaults: {
          audioPreset: { maxBitrate: credentials.limits.audio.maxBitrate },
          dtx: true,
          red: true,
          videoCodec: 'vp9' as VideoCodec,
        },
      })

      attachRoomListeners(room, set, get)

      await room.connect(credentials.url, credentials.token, { autoSubscribe: true })

      /**
       * Publica o microfone já processado, em vez de deixar o SDK abrir o
       * dispositivo. É isso que permite passar o sinal pelo RNNoise antes de
       * ele sair da máquina.
       *
       * Falhar aqui não impede a entrada. Quem não deu permissão, quem está num
       * computador sem microfone ou com ele ocupado por outro programa ainda
       * consegue ouvir a conversa, e recebe um aviso do que houve. Abortar a
       * entrada inteira transformava um problema local num "não consigo entrar"
       * sem explicação.
       */
      let micUnavailable: string | null = null

      try {
        processedAudio = await micProcessor.open(prefs.audio.inputDeviceId, prefs.audio)
        const audioTrack = new LocalAudioTrack(processedAudio.track, undefined, false)

        await room.localParticipant.publishTrack(audioTrack, {
          source: Track.Source.Microphone,
          audioPreset: { maxBitrate: credentials.limits.audio.maxBitrate },
          dtx: true,
          red: true,
        })

        if (prefs.audio.startMuted) {
          await room.localParticipant.setMicrophoneEnabled(false)
        }
      } catch (micError) {
        micUnavailable = describeMicrophoneError(micError)
        processedAudio = null
        await micProcessor.close()
      }

      set({
        room,
        limits: credentials.limits,
        connecting: false,
        connectionState: ConnectionState.Connected,
        muted: micUnavailable ? true : prefs.audio.startMuted,
        rnnoiseActive: processedAudio?.rnnoiseActive ?? false,
        micUnavailable,
        error: null,
      })

      startLevelMonitor(set, get)
      startStatsMonitor(set, get)

      gateway.send({
        op: 'voice_state',
        d: {
          guildId,
          channelId,
          selfMute: prefs.audio.startMuted,
          selfDeaf: false,
          screenSharing: false,
          cameraOn: false,
        },
      })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Não foi possível entrar no canal de voz'
      set({ connecting: false, error: message, channelId: null, guildId: null })
      await micProcessor.close()
      processedAudio = null
      throw error
    }
  },

  disconnect: async () => {
    const { room, channelId, guildId } = get()
    stopMonitors()
    silenciarBandeja()

    if (room) {
      await room.disconnect(true)
    }
    await micProcessor.close()
    processedAudio = null

    if (channelId) {
      void api.voice.leave(channelId).catch(() => {})
    }
    if (guildId) {
      gateway.send({
        op: 'voice_state',
        d: { guildId, channelId: null, selfMute: false, selfDeaf: false },
      })
    }

    set({
      room: null,
      channelId: null,
      guildId: null,
      connectionState: ConnectionState.Disconnected,
      remotes: {},
      localScreenTrack: null,
      localCameraTrack: null,
      screenSharing: false,
      cameraOn: false,
      localAudioLevel: 0,
      localSpeaking: false,
      micUnavailable: null,
      stats: emptyStats,
    })
  },

  /**
   * Liga ou desliga o próprio microfone.
   *
   * Abrir o microfone estando sem ouvir também devolve o som. Ninguém escolhe
   * de propósito falar sem escutar: quem toca no microfone quer voltar para a
   * conversa, e continuar surdo depois disso é o tipo de estado em que a pessoa
   * fala sozinha por um minuto até perceber.
   */
  toggleMute: async () => {
    const { room, muted, guildId, channelId, deafened } = get()
    if (!room) return

    const proximoMudo = !muted
    const proximoSurdo = proximoMudo ? deafened : false

    await room.localParticipant.setMicrophoneEnabled(!proximoMudo)

    if (deafened !== proximoSurdo) aplicarSurdez(room, proximoSurdo)

    set({ muted: proximoMudo, deafened: proximoSurdo })

    if (guildId) {
      gateway.send({
        op: 'voice_state',
        d: { guildId, channelId, selfMute: proximoMudo, selfDeaf: proximoSurdo },
      })
    }
  },

  /**
   * Ensurdecer silencia a saída e também o microfone.
   *
   * Quem não está ouvindo não deve continuar sendo ouvido: é a expectativa que
   * todo mundo tem desse botão, e o contrário gera situações constrangedoras.
   *
   * Ao voltar a ouvir, o microfone volta ao que era antes. Quem já estava mudo
   * continua mudo; quem estava falando volta a falar, sem ter que reparar que o
   * botão ficou para trás.
   */
  toggleDeafen: () => {
    const { room, deafened, muted, guildId, channelId, mudoAntesDeEnsurdecer } = get()
    if (!room) return

    const proximoSurdo = !deafened
    const proximoMudo = proximoSurdo ? true : mudoAntesDeEnsurdecer

    aplicarSurdez(room, proximoSurdo)
    // Um som já tocando também precisa calar: parar de ouvir a sala e continuar
    // escutando a brincadeira de alguém seria o mesmo bug de sempre, por outro
    // caminho.
    if (proximoSurdo) silenciarBandeja()
    void room.localParticipant.setMicrophoneEnabled(!proximoMudo)

    set({
      deafened: proximoSurdo,
      muted: proximoMudo,
      mudoAntesDeEnsurdecer: proximoSurdo ? muted : false,
    })

    if (guildId) {
      gateway.send({
        op: 'voice_state',
        d: { guildId, channelId, selfMute: proximoMudo, selfDeaf: proximoSurdo },
      })
    }
  },

  toggleCamera: async () => {
    const { room, cameraOn, limits, guildId, channelId, muted, deafened } = get()
    if (!room || !limits) return

    const next = !cameraOn

    if (next) {
      const prefs = usePreferencesStore.getState()
      await room.localParticipant.setCameraEnabled(true, {
        deviceId: prefs.video.inputDeviceId,
        resolution: {
          width: limits.camera.maxWidth,
          height: limits.camera.maxHeight,
          frameRate: limits.camera.maxFramerate,
        },
      })
      const publication = room.localParticipant.getTrackPublication(Track.Source.Camera)
      set({ localCameraTrack: (publication?.track as LocalVideoTrack) ?? null })
    } else {
      await room.localParticipant.setCameraEnabled(false)
      set({ localCameraTrack: null })
    }

    set({ cameraOn: next })

    if (guildId) {
      gateway.send({
        op: 'voice_state',
        d: { guildId, channelId, selfMute: muted, selfDeaf: deafened, cameraOn: next },
      })
    }
  },

  /**
   * Compartilhamento de tela.
   *
   * Os perfis vão de 540p a 1080p, e só o de jogo sobe para sessenta quadros.
   * Quinze quadros já existiram aqui e foram embora: economizavam banda, mas o
   * resultado parece travado mesmo quando está tudo certo.
   *
   * O teto do servidor tem a palavra final sobre os quatro números. Um perfil
   * que peça mais do que o administrador permite é cortado sem aviso, e é por
   * isso que o teto padrão acompanha o perfil mais alto que existe.
   */
  startScreenShare: async (preset = 'smooth', fonte = { tipo: 'tela' } as FonteDeTransmissao) => {
    const { room, limits, guildId, channelId, muted, deafened } = get()
    if (!room || !limits) return

    // O preset de reserva é nomeado, não o primeiro da lista: a ordem existe para
    // apresentar da maior qualidade para a menor, e cair na primeira posição
    // faria a reserva mudar sozinha se um preset novo entrasse no topo.
    const profile =
      SCREEN_SHARE_PRESETS.find((p) => p.id === preset) ??
      SCREEN_SHARE_PRESETS.find((p) => p.id === 'smooth')!

    // O servidor tem a palavra final sobre o teto: o preset nunca ultrapassa o
    // que o administrador configurou.
    const width = Math.min(profile.width, limits.screenShare.maxWidth)
    const height = Math.min(profile.height, limits.screenShare.maxHeight)
    const framerate = Math.min(profile.framerate, limits.screenShare.maxFramerate)
    const bitrate = Math.min(profile.bitrate, limits.screenShare.maxBitrate)

    /*
      As mesmas opções de publicação valem para as duas fontes.

      É esse o ponto de transmitir uma placa de captura como tela, e não como
      câmera: ela herda o teto de qualidade da tela, que é bem mais alto, e a
      preferência por manter os quadros quando a banda aperta.
    */
    const opcoesDePublicacao = {
      videoEncoding: { maxBitrate: bitrate, maxFramerate: framerate },
      screenShareEncoding: { maxBitrate: bitrate, maxFramerate: framerate },
      // Quando a banda aperta, quem cede é a resolução. Perder quadros
      // deixa a tela com aparência de travamento, que é o que mais incomoda
      // de quem assiste.
      degradationPreference: 'maintain-framerate' as RTCDegradationPreference,
      // Simulcast em compartilhamento de tela multiplica a banda de saída
      // por camada, e quase ninguém assiste tela em miniatura.
      simulcast: false,
      videoCodec: 'vp9' as VideoCodec,
    }

    try {
      let track: LocalVideoTrack | undefined
      let somDaTransmissao: LocalAudioTrack | undefined

      if (fonte.tipo === 'dispositivo') {
        /*
          Uma entrada de vídeo publicada na posição de tela.

          O navegador entrega placa de captura e webcam pelo mesmo caminho, então
          o que muda aqui é só a posição em que a faixa é publicada. `ideal` em
          vez de `exact` porque uma placa costuma ter uma lista curta de modos
          fixos: pedir exatamente 1920 por 1080 falharia numa que só entrega
          1920 por 1200, quando aceitar o que ela tem seria melhor do que não
          transmitir nada.
        */
        const fluxo = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: fonte.deviceId },
            width: { ideal: width },
            height: { ideal: height },
            frameRate: { ideal: framerate },
          },
          audio: false,
        })

        const faixa = fluxo.getVideoTracks()[0]
        if (!faixa) throw new Error('o dispositivo não entregou imagem')

        // Diz ao codificador que a imagem se mexe o tempo todo, como um jogo.
        // Sem isso ele trata a entrada como texto parado e sacrifica quadros.
        faixa.contentHint = 'motion'

        track = new LocalVideoTrack(faixa, undefined, false)
        await room.localParticipant.publishTrack(track, {
          source: Track.Source.ScreenShare,
          ...opcoesDePublicacao,
        })

        /*
          O som da placa vem por uma entrada separada.

          Ele sobe como áudio de transmissão e não como microfone: assim escapa
          da supressão de ruído e do portão, que existem para voz e destruiriam
          música e efeito de jogo, e cai no controle de volume da transmissão em
          vez de no da voz de quem está mostrando.
        */
        if (fonte.audioDeviceId) {
          try {
            const somDoFluxo = await navigator.mediaDevices.getUserMedia({
              audio: {
                deviceId: { exact: fonte.audioDeviceId },
                // Tudo desligado: são filtros pensados para voz de pessoa.
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
              },
            })

            const faixaDeSom = somDoFluxo.getAudioTracks()[0]
            if (faixaDeSom) {
              faixaDeSom.contentHint = 'music'
              const somDaTela = new LocalAudioTrack(faixaDeSom, undefined, false)
              await room.localParticipant.publishTrack(somDaTela, {
                source: Track.Source.ScreenShareAudio,
                audioPreset: { maxBitrate: 96_000 },
                // Cortar silêncio e duplicar pacotes são economias de fala; em
                // jogo elas cobram o preço em qualidade sem quase nada em troca.
                dtx: false,
                red: false,
              })
              somDaTransmissao = somDaTela
            }
          } catch {
            // Sem o som a transmissão continua valendo: melhor imagem sem áudio
            // do que nenhuma imagem por causa de uma entrada ocupada.
          }
        }
      } else {
        // No desktop a escolha do monitor ou da janela acontece antes, na nossa
        // própria interface, e precisa chegar ao processo principal agora: é
        // ele quem responde ao pedido de captura.
        usarFonteDeCaptura(fonte.sourceId ?? null)

        await room.localParticipant.setScreenShareEnabled(
          true,
          {
            audio: true,
            resolution: { width, height, frameRate: framerate },
            contentHint: 'motion',
          },
          opcoesDePublicacao,
        )

        const publication = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)
        track = publication?.track as LocalVideoTrack | undefined
      }

      // Se o usuário parar pelo botão nativo do navegador, precisamos refletir
      // isso na interface: sem este ouvinte o botão ficaria aceso para sempre.
      track?.mediaStreamTrack.addEventListener('ended', () => {
        void get().stopScreenShare()
      })

      set({
        screenSharing: true,
        screenPreset: preset,
        localScreenTrack: track ?? null,
        localScreenAudioTrack: somDaTransmissao ?? null,
        fonteDaTransmissao: fonte,
      })

      if (guildId) {
        gateway.send({
          op: 'voice_state',
          d: { guildId, channelId, selfMute: muted, selfDeaf: deafened, screenSharing: true },
        })
      }
    } catch (error) {
      // Cancelar o seletor de janela do navegador cai aqui e não é erro.
      if (error instanceof Error && error.name === 'NotAllowedError') return

      set({
        error:
          fonte.tipo === 'dispositivo'
            ? 'Não foi possível transmitir esse dispositivo. Verifique se outro programa não está usando ele.'
            : 'Não foi possível compartilhar a tela',
      })
      throw error
    }
  },

  stopScreenShare: async () => {
    const {
      room,
      guildId,
      channelId,
      muted,
      deafened,
      localScreenTrack,
      localScreenAudioTrack,
      fonteDaTransmissao,
    } = get()
    if (!room) return

    if (fonteDaTransmissao?.tipo === 'dispositivo' && localScreenTrack) {
      /*
        Faixa publicada à mão precisa ser retirada à mão.

        `setScreenShareEnabled(false)` só conhece o que ele mesmo criou pelo
        seletor do navegador; deixar por conta dele aqui manteria a luz da placa
        de captura acesa e a imagem subindo depois do botão de parar.
      */
      await room.localParticipant.unpublishTrack(localScreenTrack, true)
      localScreenTrack.stop()

      if (localScreenAudioTrack) {
        await room.localParticipant.unpublishTrack(localScreenAudioTrack, true)
        localScreenAudioTrack.stop()
      }
    } else {
      await room.localParticipant.setScreenShareEnabled(false)
    }

    set({
      screenSharing: false,
      localScreenTrack: null,
      localScreenAudioTrack: null,
      fonteDaTransmissao: null,
    })

    if (guildId) {
      gateway.send({
        op: 'voice_state',
        d: { guildId, channelId, selfMute: muted, selfDeaf: deafened, screenSharing: false },
      })
    }
  },

  /**
   * Deixa de assistir a transmissão de alguém.
   *
   * Cancelar a inscrição, e não apenas esconder o quadro, é o que faz diferença:
   * a faixa para de chegar e a banda para de ser gasta. O som da transmissão vai
   * junto, porque quem fecha uma live não quer continuar ouvindo o jogo dela.
   *
   * A voz da pessoa continua chegando normalmente: fechar a transmissão não é
   * deixar de falar com quem transmite.
   */
  ocultarTransmissao: (identity) => {
    const { room, transmissoesOcultas } = get()
    if (!room || transmissoesOcultas.includes(identity)) return

    aplicarInscricaoDeTela(room, identity, false)
    set({ transmissoesOcultas: [...transmissoesOcultas, identity] })
  },

  assistirTransmissao: (identity) => {
    const { room, transmissoesOcultas } = get()
    if (!room) return

    aplicarInscricaoDeTela(room, identity, true)
    set({ transmissoesOcultas: transmissoesOcultas.filter((id) => id !== identity) })
  },

  /** Troca de microfone ou de filtro sem derrubar a chamada. */
  reapplyAudioProcessing: async (options) => {
    const { room } = get()
    if (!room) return

    const prefs = usePreferencesStore.getState()

    const publication = room.localParticipant.getTrackPublication(Track.Source.Microphone)
    if (publication?.track) {
      await room.localParticipant.unpublishTrack(publication.track)
    }

    /*
      O aviso de microfone indisponível precisa ser reavaliado aqui.

      Ele só era limpo ao entrar e ao sair da chamada. Quem entrava com o
      microfone ocupado por outro programa via o alerta, trocava de aparelho na
      tela de configurações, o novo abria sem problema nenhum, e mesmo assim a
      faixa vermelha continuava na tela até a chamada acabar. Uma tentativa que
      dá certo tem que apagar o aviso; uma que falha tem que atualizá-lo em vez
      de estourar sem explicação.
    */
    try {
      processedAudio = await micProcessor.open(prefs.audio.inputDeviceId, options)
    } catch (error) {
      set({ micUnavailable: describeMicrophoneError(error), muted: true })
      await room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
      return
    }

    const audioTrack = new LocalAudioTrack(processedAudio.track, undefined, false)

    await room.localParticipant.publishTrack(audioTrack, {
      source: Track.Source.Microphone,
      dtx: true,
      red: true,
    })

    if (get().muted) {
      await room.localParticipant.setMicrophoneEnabled(false)
    }

    set({ rnnoiseActive: processedAudio.rnnoiseActive, micUnavailable: null })
  },

  setOutputDevice: async (deviceId) => {
    const { room } = get()
    if (!room) return
    await room.switchActiveDevice('audiooutput', deviceId)
  },
}))

// ---------------------------------------------------------------------------
// Ouvintes da sala
// ---------------------------------------------------------------------------

type SetState = (partial: Partial<VoiceState> | ((state: VoiceState) => Partial<VoiceState>)) => void
type GetState = () => VoiceState

function emptyRemote(identity: string): RemoteMedia {
  return {
    identity,
    audioTrack: null,
    cameraTrack: null,
    screenTrack: null,
    screenAudioTrack: null,
    speaking: false,
    audioLevel: 0,
    connectionQuality: ConnectionQuality.Unknown,
  }
}

function attachRoomListeners(room: Room, set: SetState, get: GetState): void {
  room.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
    set((state) => ({
      remotes: { ...state.remotes, [participant.identity]: emptyRemote(participant.identity) },
    }))
  })

  room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
    set((state) => {
      const remotes = { ...state.remotes }
      delete remotes[participant.identity]
      return {
        remotes,
        transmissoesOcultas: state.transmissoesOcultas.filter(
          (id) => id !== participant.identity,
        ),
      }
    })
  })

  /*
    Fechar uma transmissão vale para aquela transmissão, não para a pessoa.

    Quando ela para de transmitir, a escolha morre junto: se começar outra coisa
    depois, aparece normalmente. Bloquear alguém para sempre seria outra decisão,
    e não é a que se toma ao clicar num "x" para liberar espaço na tela.
  */
  room.on(
    RoomEvent.TrackUnpublished,
    (publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (publication.source !== Track.Source.ScreenShare) return

      set((state) => ({
        transmissoesOcultas: state.transmissoesOcultas.filter(
          (id) => id !== participant.identity,
        ),
      }))
    },
  )

  room.on(
    RoomEvent.TrackSubscribed,
    (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      set((state) => {
        const existing = state.remotes[participant.identity] ?? emptyRemote(participant.identity)
        const next = { ...existing }

        if (publication.source === Track.Source.Microphone) next.audioTrack = track
        else if (publication.source === Track.Source.Camera) next.cameraTrack = track
        else if (publication.source === Track.Source.ScreenShare) next.screenTrack = track
        else if (publication.source === Track.Source.ScreenShareAudio) next.screenAudioTrack = track

        return { remotes: { ...state.remotes, [participant.identity]: next } }
      })

      // Quem está ensurdecido não deve voltar a ouvir por causa de uma faixa
      // que chegou depois.
      if (get().deafened && publication.kind === Track.Kind.Audio) {
        publication.setEnabled(false)
      }
    },
  )

  room.on(
    RoomEvent.TrackUnsubscribed,
    (_track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      set((state) => {
        const existing = state.remotes[participant.identity]
        if (!existing) return {}
        const next = { ...existing }

        if (publication.source === Track.Source.Microphone) next.audioTrack = null
        else if (publication.source === Track.Source.Camera) next.cameraTrack = null
        else if (publication.source === Track.Source.ScreenShare) next.screenTrack = null
        else if (publication.source === Track.Source.ScreenShareAudio) next.screenAudioTrack = null

        return { remotes: { ...state.remotes, [participant.identity]: next } }
      })
    },
  )

  room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
    const speakingIds = new Set(speakers.map((s) => s.identity))
    const localIdentity = room.localParticipant.identity

    set((state) => {
      const remotes = { ...state.remotes }
      for (const [identity, media] of Object.entries(remotes)) {
        const speaking = speakingIds.has(identity)
        if (media.speaking !== speaking) remotes[identity] = { ...media, speaking }
      }
      return { remotes, localSpeaking: speakingIds.has(localIdentity) }
    })
  })

  room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
    if (participant.identity === room.localParticipant.identity) return
    set((state) => {
      const existing = state.remotes[participant.identity]
      if (!existing) return {}
      return {
        remotes: {
          ...state.remotes,
          [participant.identity]: { ...existing, connectionQuality: quality },
        },
      }
    })
  })

  room.on(RoomEvent.ConnectionStateChanged, (connectionState) => {
    set({ connectionState })
  })

  room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
    stopMonitors()
    set({
      room: null,
      channelId: null,
      guildId: null,
      remotes: {},
      screenSharing: false,
      cameraOn: false,
      localScreenTrack: null,
      localCameraTrack: null,
      connectionState: ConnectionState.Disconnected,
      error:
        reason === DisconnectReason.ROOM_DELETED
          ? 'A sala foi encerrada'
          : reason === DisconnectReason.PARTICIPANT_REMOVED
            ? 'Você foi desconectado por um moderador'
            : null,
    })
    void micProcessor.close()
    processedAudio = null
  })

  room.on(RoomEvent.MediaDevicesError, (error: Error) => {
    set({ error: `Problema com o dispositivo de áudio: ${error.message}` })
  })

  // Popula os participantes que já estavam na sala antes desta conexão.
  const initial: Record<string, RemoteMedia> = {}
  for (const participant of room.remoteParticipants.values()) {
    initial[participant.identity] = emptyRemote(participant.identity)
  }
  if (Object.keys(initial).length > 0) set({ remotes: initial })
}

/** Amostra o nível do microfone para o indicador de fala. */
function startLevelMonitor(set: SetState, get: GetState): void {
  if (levelTimer !== null) clearInterval(levelTimer)

  levelTimer = window.setInterval(() => {
    if (!processedAudio) return
    const level = get().muted ? 0 : processedAudio.getLevel()
    set({ localAudioLevel: level })
  }, 100)
}

/**
 * Estatísticas de rede da própria conexão.
 *
 * Interessa ao usuário saber quanto está gastando de banda ao transmitir, e ao
 * suporte saber a perda de pacotes quando alguém reclama de áudio picotado.
 */
function startStatsMonitor(set: SetState, get: GetState): void {
  if (statsTimer !== null) clearInterval(statsTimer)

  let lastOutbound = 0
  let lastInbound = 0
  let lastAt = Date.now()

  statsTimer = window.setInterval(() => {
    const room = get().room
    if (!room) return

    void (async () => {
      let outboundBytes = 0
      let inboundBytes = 0
      let packetsLost = 0
      let packetsTotal = 0
      let roundTripMs = 0

      const publications = [
        ...room.localParticipant.trackPublications.values(),
      ]

      for (const publication of publications) {
        const track = publication.track
        if (!track) continue
        try {
          const report = await track.getRTCStatsReport?.()
          report?.forEach((stat) => {
            if (stat.type === 'outbound-rtp') {
              outboundBytes += (stat as { bytesSent?: number }).bytesSent ?? 0
            }
            if (stat.type === 'candidate-pair' && (stat as { nominated?: boolean }).nominated) {
              const rtt = (stat as { currentRoundTripTime?: number }).currentRoundTripTime
              if (rtt) roundTripMs = Math.round(rtt * 1000)
            }
          })
        } catch {
          // Estatística indisponível não deve derrubar o monitor.
        }
      }

      for (const participant of room.remoteParticipants.values()) {
        for (const publication of participant.trackPublications.values()) {
          const track = publication.track
          if (!track) continue
          try {
            const report = await track.getRTCStatsReport?.()
            report?.forEach((stat) => {
              if (stat.type === 'inbound-rtp') {
                const s = stat as { bytesReceived?: number; packetsLost?: number; packetsReceived?: number }
                inboundBytes += s.bytesReceived ?? 0
                packetsLost += s.packetsLost ?? 0
                packetsTotal += (s.packetsReceived ?? 0) + (s.packetsLost ?? 0)
              }
            })
          } catch {
            // Idem.
          }
        }
      }

      const now = Date.now()
      const elapsedSec = Math.max(0.001, (now - lastAt) / 1000)

      const outboundBps = Math.max(0, Math.round(((outboundBytes - lastOutbound) * 8) / elapsedSec))
      const inboundBps = Math.max(0, Math.round(((inboundBytes - lastInbound) * 8) / elapsedSec))

      lastOutbound = outboundBytes
      lastInbound = inboundBytes
      lastAt = now

      set({
        stats: {
          outboundBps,
          inboundBps,
          packetsLostPercent: packetsTotal > 0 ? Math.round((packetsLost / packetsTotal) * 1000) / 10 : 0,
          roundTripMs,
        },
      })
    })()
  }, 2000)
}

function stopMonitors(): void {
  if (levelTimer !== null) {
    clearInterval(levelTimer)
    levelTimer = null
  }
  if (statsTimer !== null) {
    clearInterval(statsTimer)
    statsTimer = null
  }
}

/**
 * Reanuncia a presença na sala sempre que o gateway volta.
 *
 * A sessão de voz vive no SFU e sobrevive à queda do WebSocket, mas a lista de
 * quem está no canal vive no gateway. Sem este reanúncio, uma oscilação de rede
 * tirava a pessoa da barra lateral de todo mundo enquanto ela continuava falando
 * normalmente, e só recarregar a página trazia de volta.
 */
export function anunciarVozAoReconectar(): () => void {
  return gateway.onEvent((event) => {
    if (event.op !== 'ready') return

    const { room, guildId, channelId, muted, deafened, screenSharing, cameraOn } =
      useVoiceStore.getState()
    if (!room || !guildId || !channelId) return

    gateway.send({
      op: 'voice_state',
      d: {
        guildId,
        channelId,
        selfMute: muted,
        selfDeaf: deafened,
        screenSharing,
        cameraOn,
      },
    })
  })
}
